import { useState } from "react";

import { JsonView } from "@/components/json-view";
import { Panel } from "@/components/panel";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { apiGet } from "@/lib/api-client";
import { asRecord } from "@/lib/type-utils";

const YOUTUBE_URL_RE = /^(https?:\/\/)?(www\.)?(youtube\.com|youtu\.be)\//i;

export function YoutubeAnalysisPage() {
  const [searchQuery, setSearchQuery] = useState("");
  const [analyzing, setAnalyzing] = useState(false);
  const [analysisResult, setAnalysisResult] = useState<unknown>(null);
  const [analysisError, setAnalysisError] = useState<string | null>(null);

  const result = asRecord(analysisResult);
  const transcript = typeof result.transcript === "string" ? result.transcript : "";
  const wordCount = Number(result.word_count ?? 0);

  const analyzeVideo = async () => {
    const url = searchQuery.trim();
    if (!url || analyzing) return;
    if (!YOUTUBE_URL_RE.test(url)) {
      setAnalysisError("Please enter a valid YouTube URL.");
      setAnalysisResult(null);
      return;
    }
    setAnalyzing(true);
    setAnalysisError(null);
    try {
      const data = await apiGet<unknown>(
        `/api/youtube/analyze?url=${encodeURIComponent(url)}`,
      );
      // The endpoint returns 200 even on failure: { video_url, error, transcript: null }.
      const record = asRecord(data);
      if (typeof record.error === "string" && record.error) {
        setAnalysisError(record.error);
        setAnalysisResult(null);
      } else {
        setAnalysisResult(data);
      }
    } catch (error) {
      setAnalysisError(error instanceof Error ? error.message : "Analysis failed");
      setAnalysisResult(null);
    } finally {
      setAnalyzing(false);
    }
  };

  return (
    <div className="space-y-4 md:space-y-6">
      <Panel
        title="YouTube Analysis"
        description="Single-video transcription for market research."
      >
        <div className="rounded-lg bg-gunmetal/50 p-4 text-sm text-slate-400">
          Aggregate YouTube sentiment and channel leaderboards are not connected
          to a backend endpoint yet. This page only calls the live transcription
          endpoint below.
        </div>
      </Panel>

      <Panel
        title="Transcribe Video"
        description="Transcribe a YouTube video by URL for market research"
        actions={
          <div className="flex items-center gap-2">
            <Input
              type="text"
              placeholder="YouTube video URL..."
              className="text-sm"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !analyzing) void analyzeVideo();
              }}
            />
            <Button
              className="text-sm"
              onClick={analyzeVideo}
              disabled={analyzing || !searchQuery.trim()}
            >
              {analyzing ? "Transcribing..." : "Transcribe"}
            </Button>
          </div>
        }
      >
        {analysisError ? (
          <div className="rounded-lg bg-red-500/10 p-4 text-sm text-red-400">
            {analysisError}
          </div>
        ) : transcript ? (
          <div className="space-y-2">
            <div className="text-xs text-slate-400">{wordCount.toLocaleString()} words</div>
            <p className="max-h-[300px] overflow-y-auto whitespace-pre-wrap rounded-lg bg-gunmetal/50 p-3 text-sm text-slate-300">
              {transcript}
            </p>
          </div>
        ) : (
          <div className="rounded-lg bg-gunmetal/50 p-4 text-center text-sm text-slate-400">
            Enter a YouTube video URL and click Transcribe.
          </div>
        )}
      </Panel>

      <Panel title="Transcription API" description="Raw response from the last transcription">
        <JsonView value={analysisResult} />
      </Panel>
    </div>
  );
}
